import { useAuth0Client } from '@/composables/useAuth0Client';
import { OVERVIEW_PAGE_PATH, VALUATION_FORM_PATH } from '@/router/routes/constants';
import { trackingGenericInteractionEvent } from '@/tracking/tracking';
import { TRACKING_ACTION } from '@/tracking/types';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'vuex';

const SURVEY_STORAGE_KEY_PREFIX = 'emptyState_surveyLastTriggered_';
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const useEmptyStateSurvey = () => {
  const { locale } = useI18n();
  const store = useStore();
  const auth0Client = useAuth0Client();

  const username = computed(() => store.getters['common/getUsername']);
  const hasAttemptedToLeaveOnce = ref(false);

  const isWhiteListedUser = computed(
    () =>
      auth0Client?.user.value?.email?.endsWith('@swissmarketplace.group') ||
      auth0Client?.user.value?.email === 'jkp31081@laoia.com',
  );

  const wasSurveyTriggeredWithinLastWeek = (): boolean => {
    const userId = username.value;
    if (!userId) return false;

    const lastTriggered = localStorage.getItem(`${SURVEY_STORAGE_KEY_PREFIX}${userId}`);
    if (!lastTriggered) return false;

    return Date.now() - Number(lastTriggered) < ONE_WEEK_MS;
  };

  const shouldShowSurvey = computed(() => {
    return (
      isWhiteListedUser.value &&
      !wasSurveyTriggeredWithinLastWeek() &&
      !hasAttemptedToLeaveOnce.value
    );
  });

  const triggerSurvey = () => {
    if (!shouldShowSurvey.value) return;

    trackingGenericInteractionEvent(TRACKING_ACTION.EXIT_HOME_OWNER_WORLD);

    const userId = username.value;
    if (userId) {
      localStorage.setItem(`${SURVEY_STORAGE_KEY_PREFIX}${userId}`, String(Date.now()));
    }
  };

  // Route leave guard (in-app navigation)
  onBeforeRouteLeave((to) => {
    if (!shouldShowSurvey.value) {
      return;
    }

    // Ignore when navigating to the valuation form
    if (to.path === VALUATION_FORM_PATH[locale.value]) {
      return;
    }
    // Ignore when changing locale, as it triggers a route change
    if (Object.values(OVERVIEW_PAGE_PATH).includes(to.path)) {
      return;
    }
    triggerSurvey();
    hasAttemptedToLeaveOnce.value = true;
    return false;
  });

  // Browser-level navigation (tab close, refresh, browser back to external page)
  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!shouldShowSurvey.value) {
      return;
    }

    triggerSurvey();
    hasAttemptedToLeaveOnce.value = true;
    event.preventDefault();
    event.returnValue = ''; // Required for Chrome/Edge compatibility
  };

  onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  });
};
